import { useEffect, useState, type FormEvent } from 'react';
import { IconPin } from '../components/Icons';
import { Link, useNavigate } from 'react-router-dom';
import { meApi, referenceApi } from '../api/endpoints';
import type { Locality, PropertySummary } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { PropertyCard } from '../components/PropertyCard';
import { LocationAutocomplete } from '../components/LocationAutocomplete';

type Category = 'pg' | 'homes' | 'hotels';

export function HomePage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [category, setCategory] = useState<Category>('pg');
  const [locality, setLocality] = useState('');
  const [localities, setLocalities] = useState<Locality[]>([]);
  const [recent, setRecent] = useState<PropertySummary[]>([]);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  useEffect(() => {
    referenceApi.localities().then(setLocalities).catch(() => setLocalities([]));
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setRecent([]);
      return;
    }

    meApi.recentlyViewed(4).then(setRecent).catch(() => setRecent([]));
  }, [isAuthenticated]);

  const search = (event: FormEvent) => {
    event.preventDefault();

    const params = new URLSearchParams();
    if (locality.trim()) params.set('locality', locality.trim());

    navigate(`/${category}?${params.toString()}`);
  };

  /**
   * Location is requested only when the customer asks for it, never on page load, and a
   * refusal simply leaves the manual locality box as the way forward.
   */
  const useMyLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('This browser cannot share your location. Type a locality instead.');
      return;
    }

    setLocating(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false);
        const params = new URLSearchParams({
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6),
          radiusKm: '5',
          sort: 'distance',
        });
        navigate(`/${category}?${params.toString()}`);
      },
      () => {
        setLocating(false);
        setLocationError('We could not get your location. Type a locality instead.');
      },
      { timeout: 10000, maximumAge: 300000 },
    );
  };

  return (
    <>
      <section className="sf-hero">
        <div className="container">
          <div className="row">
            <div className="col-12 col-lg-8">
              <h1 className="mb-2">Find your next place</h1>
              <p className="lead mb-4 opacity-75">
                PG and hostels, homes for rent, and hotels — searched in one place.
              </p>

              <form className="sf-card sf-card-pad" onSubmit={search}>
                <div className="btn-group mb-3 w-100" role="group" aria-label="What are you looking for?">
                  {(
                    [
                      ['pg', 'PG & Hostels'],
                      ['homes', 'Homes'],
                      ['hotels', 'Hotels'],
                    ] as [Category, string][]
                  ).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={`btn ${category === value ? 'btn-primary' : 'btn-outline-secondary'}`}
                      aria-pressed={category === value}
                      onClick={() => setCategory(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <label className="form-label" htmlFor="home-locality">
                  Where are you looking?
                </label>

                <div className="d-flex flex-column flex-sm-row gap-2">
                  <LocationAutocomplete
                    id="home-locality"
                    value={locality}
                    options={localities}
                    placeholder="Locality or city"
                    onChange={setLocality}
                    onCommit={setLocality}
                  />

                  <button type="submit" className="btn btn-primary px-4">
                    Search
                  </button>
                </div>

                <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-decoration-none px-0"
                    onClick={useMyLocation}
                    disabled={locating}
                  >
                    {locating ? 'Finding you…' : <><IconPin /> Use my current location</>}
                  </button>
                  <span className="form-text mb-0">
                    We only ask for your location when you tap this.
                  </span>
                </div>

                {locationError && (
                  <div className="alert alert-warning py-2 px-3 mt-2 mb-0" role="alert">
                    {locationError}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="container py-4">
        <h2 className="h4 mb-3">Browse by category</h2>

        <div className="row g-3">
          <div className="col-12 col-md-4">
            <Link to="/pg" className="sf-category-tile sf-category-tile--pg">
              <h3>PG &amp; Hostels</h3>
              <p>Monthly stays with food, shared or single rooms, deposits shown up front.</p>
            </Link>
          </div>

          <div className="col-12 col-md-4">
            <Link to="/homes" className="sf-category-tile sf-category-tile--home">
              <h3>Homes for Rent</h3>
              <p>Independent houses, apartments and villas, listed one property at a time.</p>
            </Link>
          </div>

          <div className="col-12 col-md-4">
            <Link to="/hotels" className="sf-category-tile sf-category-tile--hotel">
              <h3>Hotels &amp; Short Stays</h3>
              <p>Budget to premium rooms with nightly prices and the taxes included.</p>
            </Link>
          </div>
        </div>
      </section>

      {localities.length > 0 && (
        <section className="container py-3">
          <h2 className="h5 mb-3">Popular localities</h2>
          <div className="d-flex flex-wrap gap-2">
            {localities.slice(0, 14).map((l) => (
              <Link key={`${l.city}-${l.locality}`} to={`/pg?locality=${encodeURIComponent(l.locality)}`} className="sf-chip">
                {l.locality} <span className="sf-muted">({l.listingCount})</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {recent.length > 0 && (
        <section className="container py-4">
          <h2 className="h5 mb-3">Recently viewed</h2>
          <div className="row g-3">
            {recent.map((property) => (
              <div className="col-12 col-sm-6 col-lg-3" key={property.id}>
                <PropertyCard property={property} showDistance={false} />
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="container py-4">
        <div className="sf-card sf-card-pad">
          <h2 className="h5">Listing a property?</h2>
          <p className="mb-3 sf-muted">
            PG and hotel branches are listed on a monthly subscription, each branch billed separately. Homes are
            listed one at a time with a single listing fee.
          </p>
          <Link to="/register?type=owner" className="btn btn-primary">
            Get started
          </Link>
          <Link to="/pricing" className="btn btn-link">
            See pricing
          </Link>
        </div>
      </section>
    </>
  );
}
